// hooks/playground/useSectionCollapse.js
import { useState, useCallback, useEffect } from 'react';

export const useSectionCollapse = (organizedData, lineItems, groupBySubcategory) => {
  const [collapsedSections, setCollapsedSections] = useState(new Set());

  // Auto-collapse all subcategories on first render
  useEffect(() => {
    if (lineItems.length > 0 && collapsedSections.size === 0) {
      const subcategories = new Set();
      
      const allGroups = [
        ...Object.keys(groupBySubcategory(organizedData.income)).map(s => `income-${s}`),
        ...Object.keys(groupBySubcategory(organizedData.cogs)).map(s => `cogs-${s}`),
        ...Object.keys(groupBySubcategory(organizedData.expenses)).map(s => `expense-${s}`),
        ...Object.keys(groupBySubcategory(organizedData.otherIncome)).map(s => `other-income-${s}`),
        ...Object.keys(groupBySubcategory(organizedData.otherExpenses)).map(s => `other-expenses-${s}`),
      ];
      
      allGroups.forEach(key => subcategories.add(key));
      setCollapsedSections(subcategories);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lineItems]);

  const toggleSection = useCallback((sectionKey) => {
    setCollapsedSections(prev => {
      const newSet = new Set(prev);
      if (newSet.has(sectionKey)) {
        newSet.delete(sectionKey);
      } else {
        newSet.add(sectionKey);
      }
      return newSet;
    });
  }, []);

  const isCollapsed = useCallback((sectionKey) => collapsedSections.has(sectionKey), [collapsedSections]);

  const getDetailLevel = useCallback(() => {
    const mainSections = [
      'main-income',
      'main-cogs',
      'main-expenses',
      'main-other-income',
      'main-other-expenses'
    ];
    
    const mainCollapsed = mainSections.some(section => collapsedSections.has(section));
    
    if (mainCollapsed) {
      return 'sections';
    }
    
    const hasCollapsedSubcategories = Array.from(collapsedSections).some(key => 
      !mainSections.includes(key) && key !== 'ami-breakdown'
    );
    
    if (hasCollapsedSubcategories) {
      return 'categories';
    }
    
    return 'expanded';
  }, [collapsedSections]);

  return {
    collapsedSections,
    setCollapsedSections,
    toggleSection,
    isCollapsed,
    getDetailLevel,
  };
};